import React, { useEffect, useMemo, useRef, useState } from "react";
// @ts-ignore: librería copiada en src/vendor (sin tipos)
import qrcode from "../../../../vendor/qrcode-generator";

import useEsTelefono from "../../../../utils/esTelefono";

import HttpManager from "../../../../services/HttpManager";
import { AttendeeKind, BajaDestino, BajaPreviewDTO, EventAttendeeDTO, EventDetailDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { BuscarAlumna, Modal, PromptModal, blobErrorText, dinero, dineroCod, errorText, guardarBlob, horaLocal, useRolDinero } from "../workshops/comun";

interface Props { campusId: number; evento: EventDetailDTO; soloLectura: boolean; reload: () => void; notify: (t: string, ok?: boolean) => void; }

const slug = (t: string) => t.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "");
const KINDS: AttendeeKind[] = ["graduada", "participante", "invitado", "especial", "externo"];
/** Tipos que se eligen como alumna (participante y especial también pueden ser alumnas; externo opcionalmente). */
const CON_ALUMNA: AttendeeKind[] = ["graduada", "participante", "especial", "externo"];
const SE_COBRA: AttendeeKind[] = ["graduada", "participante", "invitado"];

/** Lo que pagan n invitados de una graduada: el paquete más barato que cabe + el resto a precio de invitado (igual que el servidor). */
const totalInvitados = (ev: EventDetailDTO, n: number): number => {
	if (n <= 0) return 0;
	let mejor = n * ev.extraGuestPrice;
	(ev.guestTiers || []).forEach(t => { if (t.guests <= n) mejor = Math.min(mejor, t.total + (n - t.guests) * ev.extraGuestPrice); });
	return Math.round(mejor * 100) / 100;
};

/** Precio del siguiente invitado de una graduada y si un niño entraría gratis por el paquete. */
const precioInvitado = (ev: EventDetailDTO, hostId: number, nino: boolean): { precio: number; lugar: number; paquete: string | null } => {
	const suyos = ev.attendees.filter(a => a.status === 1 && a.kind === "invitado" && a.hostAttendeeId === hostId);
	const adultos = suyos.filter(a => !a.isChild && a.charge && a.charge.statusId !== 4).length;
	const tope = [...(ev.guestTiers || [])].reverse().find(t => t.guests <= adultos + (nino ? 0 : 1));
	const paquete = tope ? S.paqueteCorto(tope.guests, dinero(ev.currency, tope.total), tope.freeChild) : null;
	if (!nino) return { precio: Math.round((totalInvitados(ev, adultos + 1) - totalInvitados(ev, adultos)) * 100) / 100, lugar: adultos + 1, paquete };
	const ninosGratis = (ev.guestTiers || []).some(t => t.guests <= adultos && t.freeChild) ? 1 : 0;
	return { precio: suyos.filter(a => a.freeByPackage).length < ninosGratis ? 0 : ev.childPrice, lugar: 0, paquete };
};

/** QR del asistente dibujado en el navegador (mismo vendor que "Usar el celular" en Matrículas). */
const dibujarQr = (texto: string): string => {
	const qr = qrcode(0, "M");
	qr.addData(texto);
	qr.make();
	return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
};

const IconoQr: React.FC = () => (
	<svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
		<path d="M1 1h6v6H1V1zm1.5 1.5v3h3v-3h-3zM9 1h6v6H9V1zm1.5 1.5v3h3v-3h-3zM1 9h6v6H1V9zm1.5 1.5v3h3v-3h-3zM9 9h2v2H9V9zm2 2h2v2h-2v-2zm2-2h2v2h-2V9zm0 4h2v2h-2v-2zM9 13h2v2H9v-2z" />
	</svg>
);

/** Alta de graduada, participante, invitado, especial o externo. */
/** hostInicial: "+ Invitados" de una graduada (subir su paquete): abre en Invitado con ella elegida. */
const AgregarModal: React.FC<{ campusId: number; evento: EventDetailDTO; rolDinero: boolean; hostInicial?: number; onClose: () => void; onDone: (n: number) => void }> = ({ campusId, evento, rolDinero, hostInicial, onClose, onDone }) => {
	const http = HttpManager.getInstance();
	const graduadas = evento.attendees.filter(a => a.status === 1 && a.kind === "graduada");
	const [kind, setKind] = useState<AttendeeKind>(hostInicial || graduadas.length ? "invitado" : "graduada");
	const [alumna, setAlumna] = useState<{ id: number; name: string } | null>(null);
	const [name, setName] = useState<string>("");
	const [cuantos, setCuantos] = useState<string>("1");
	const [host, setHost] = useState<number>(hostInicial || 0);
	const [isChild, setIsChild] = useState<boolean>(false);
	const [tableNo, setTableNo] = useState<string>("");
	const [cobra, setCobra] = useState<boolean>(true);
	const [price, setPrice] = useState<string>("");
	const [priceNote, setPriceNote] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	// Precio del evento para este tipo (el servidor usa el mismo criterio): paquete, invitado / niño, especial sin costo
	// Invitados: varios de una vez, todos de la misma graduada. Los nombres son opcionales: la graduada paga el paquete antes
	// de saber quién va; los que faltan quedan "Invitado 2 de Wendy" y el nombre se pone después en la lista.
	const nombres = kind === "invitado" ? name.split("\n").map(x => x.trim()).filter(x => x.length > 1) : [];
	const total = kind === "invitado" ? Math.min(20, Math.max(nombres.length, Math.floor(Number(cuantos) || 0))) : 1;
	const varios = total > 1;
	const delPaquete = kind === "invitado" && host > 0 && (evento.guestTiers || []).length > 0 ? precioInvitado(evento, host, isChild) : null;
	const precioEvento = kind === "graduada" || kind === "participante" ? evento.packagePrice : kind === "especial" ? 0 : delPaquete ? delPaquete.precio : (isChild ? evento.childPrice : evento.extraGuestPrice);
	const conPrecio = !varios && (kind === "externo" || (SE_COBRA.includes(kind) && cobra));
	const precioDistinto = conPrecio && price.trim() !== "" && !isNaN(Number(price)) && Math.abs(Number(price) - precioEvento) > 0.004;
	const nombreListo = kind === "invitado" ? total > 0 : name.trim().length > 1;
	const valido = kind === "graduada" || kind === "participante" ? !!alumna
		: kind === "invitado" ? host > 0 && nombreListo
			: (!!alumna || nombreListo);
	const puedeGuardar = valido && (!precioDistinto || priceNote.trim().length > 1);

	const guardar = async () => {
		setBusy(true); setError(null);
		const uno = (nombre: string | undefined) => http.eventsService.addAttendee(campusId, {
			eventId: evento.id, kind, studentId: alumna && CON_ALUMNA.includes(kind) ? alumna.id : undefined,
			name: nombre, namePending: kind === "invitado" && !nombre ? true : undefined,
			hostAttendeeId: kind === "invitado" ? host : undefined, isChild: kind === "invitado" ? isChild : undefined,
			tableNo: tableNo.trim() || undefined, charge: SE_COBRA.includes(kind) ? cobra : undefined,
			price: conPrecio && price.trim() !== "" ? Number(price) : undefined,
			priceNote: precioDistinto ? priceNote.trim() : undefined
		});
		// Uno tras otro: cada invitado toma el precio de su lugar en el paquete
		const lista: (string | undefined)[] = kind === "invitado" ? [...nombres, ...Array(total - nombres.length).fill(undefined)] : [name.trim() || undefined];
		let hechos = 0;
		try {
			for (const n of lista) { await uno(n); hechos++; }
			onDone(hechos);
		} catch (e) {
			setError((hechos ? S.invitadosAgregados(hechos) + " " : "") + errorText(e));
			if (hechos) { setName(nombres.slice(hechos).join("\n")); setCuantos(String(total - hechos)); }
			setBusy(false);
		}
	};
	const cambiarTipo = (k: AttendeeKind) => { setKind(k); setAlumna(null); setPrice(""); setPriceNote(""); };

	return (
		<Modal title={S.agregarAsistente} onClose={onClose} ancho={560}>
			<div className="wau-chips" role="group">
				{KINDS.map(k => (
					<button key={k} type="button" className="wau-chip" aria-pressed={kind === k} onClick={() => cambiarTipo(k)}>{S.kinds[k]}</button>
				))}
			</div>
			{CON_ALUMNA.includes(kind) ? (
				alumna ? <div className="wau-wa"><b>{alumna.name}</b><button type="button" className="wau-btn s mini" onClick={() => setAlumna(null)}>{S.quitar}</button></div>
					: <div style={{ marginBottom: 10 }}><BuscarAlumna campusId={campusId} onPick={setAlumna} placeholder={S.buscarAlumna} boton={S.buscarBtn} /></div>
			) : null}
			{kind === "invitado" ? (
				<label className="wau-campo" style={{ marginBottom: 10 }}><span className="wau-lab">{S.invitaGraduada}</span>
					<select className="wau-entrada" value={host} onChange={e => setHost(Number(e.target.value))}>
						<option value={0}>{S.elegir}</option>
						{graduadas.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
					</select></label>
			) : null}
			{kind === "invitado" ? (
				<div className="wau-fila" style={{ marginBottom: 10, alignItems: "flex-start" }}>
					<label className="wau-campo" style={{ width: 80 }}><span className="wau-lab">{S.cuantos}</span>
						<input type="number" min={1} max={20} step={1} className="wau-entrada wau-num" value={total > nombres.length ? cuantos : String(total)} onChange={e => setCuantos(e.target.value)} /></label>
					<label className="wau-campo" style={{ flex: "1 1 200px" }}><span className="wau-lab">{S.nombresInvitados}</span>
						<textarea className="wau-entrada" rows={3} value={name} onChange={e => setName(e.target.value)} /></label>
				</div>
			) : (kind === "especial" || kind === "externo") && !alumna ? (
				<label className="wau-campo" style={{ marginBottom: 10 }}><span className="wau-lab">{S.colNombre}</span>
					<input className="wau-entrada" value={name} onChange={e => setName(e.target.value)} /></label>
			) : null}
			<div className="wau-fila" style={{ marginBottom: 10 }}>
				{kind === "invitado" ? <label className="wau-campo" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}><input type="checkbox" checked={isChild} onChange={e => setIsChild(e.target.checked)} /><span>{S.esNino}</span></label> : null}
				{SE_COBRA.includes(kind) ? <label className="wau-campo" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}><input type="checkbox" checked={cobra} onChange={e => setCobra(e.target.checked)} /><span>{S.seCobra}</span></label> : null}
				{conPrecio ? (
					/* Solo los roles de dinero cobran otro precio: a los demás se les muestra el del evento */
					rolDinero ? (
						<label className="wau-campo" style={{ width: 150 }}><span className="wau-lab">{S.precio} ({evento.currency?.code || ""})</span>
							<input type="number" min={0} step="0.01" className="wau-entrada wau-num" value={price} placeholder={String(precioEvento)} onChange={e => setPrice(e.target.value)} /></label>
					) : (
						<div className="wau-campo"><span className="wau-lab">{S.precioEvento}</span><span className="wau-num">{dineroCod(evento.currency, precioEvento)}</span></div>
					)
				) : null}
				<label className="wau-campo" style={{ width: 90 }}><span className="wau-lab">{S.colMesa}</span>
					<input className="wau-entrada wau-num" maxLength={10} value={tableNo} onChange={e => setTableNo(e.target.value)} /></label>
			</div>
			{delPaquete && varios && !isChild ? <p className="wau-sec" style={{ margin: "0 0 10px 0", fontSize: 13 }}>{S.totalPaquete(delPaquete.lugar - 1 + total, dinero(evento.currency, totalInvitados(evento, delPaquete.lugar - 1 + total)),
					dinero(evento.currency, totalInvitados(evento, delPaquete.lugar - 1 + total) - totalInvitados(evento, delPaquete.lugar - 1)))}</p>
				: delPaquete && !varios ? <p className="wau-sec" style={{ margin: "0 0 10px 0", fontSize: 13 }}>{isChild ? (delPaquete.precio === 0 ? S.ninoGratisAviso : "") : S.lugarInvitado(delPaquete.lugar, delPaquete.paquete)}</p> : null}
			{precioDistinto ? (
				<label className="wau-campo" style={{ marginBottom: 10 }}><span className="wau-lab">{S.motivoPrecio}</span>
					<input className="wau-entrada" maxLength={255} value={priceNote} onChange={e => setPriceNote(e.target.value)} /></label>
			) : null}
			{error ? <p className="wau-adv">{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn s" onClick={onClose} disabled={busy}>{S.volver}</button>
				<button type="button" className="wau-btn p" onClick={guardar} disabled={busy || !puedeGuardar}>{kind === "invitado" ? S.agregarVarios(total) : S.agregarBtn}</button>
			</div>
		</Modal>
	);
};

/**
 * Baja (fase 2): primero pregunta al servidor quiénes salen y cuánto pagaron. Sin pago, igual que antes (nota opcional).
 * Con pago: lo pagado, lo que se queda el evento y el sobrante, que hay que mandar a saldo, a cobros pendientes o devolver.
 */
const BajaModal: React.FC<{ campusId: number; evento: EventDetailDTO; asistente: EventAttendeeDTO; onClose: () => void; onDone: (texto: string) => void }> = ({ campusId, evento, asistente, onClose, onDone }) => {
	const http = HttpManager.getInstance();
	const [prev, setPrev] = useState<BajaPreviewDTO | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [destino, setDestino] = useState<BajaDestino | null>(null);
	const [nota, setNota] = useState<string>("");
	const [busy, setBusy] = useState<boolean>(false);

	useEffect(() => {
		let vigente = true;
		http.eventsService.bajaPreview(campusId, asistente.id)
			.then(r => { if (vigente) setPrev(r.data); })
			.catch(e => { if (vigente) setError(errorText(e)); });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, asistente.id]);

	const m = (v: number) => dinero(evento.currency, v);
	const conPago = !!prev && prev.paid > 0.004;
	const sobra = !!prev && prev.surplus > 0.004;
	const bloqueada = !!prev && prev.requiresMoneyRole && !prev.moneyRole;
	const pendientes = prev?.pendingCharges || [];
	const totalPendiente = Math.round(pendientes.reduce((t, c) => t + Number(c.pending || 0), 0) * 100) / 100;
	const notaObligatoria = sobra && destino === "devolucion";
	const valido = !!prev && !bloqueada && (!sobra || !!destino) && (!notaObligatoria || nota.trim().length >= 5);
	const destinos: BajaDestino[] = pendientes.length ? ["saldo", "aplicar", "devolucion"] : ["saldo", "devolucion"];

	const confirmar = () => {
		if (!prev) return;
		setBusy(true); setError(null);
		const d = sobra ? destino || undefined : undefined;
		http.eventsService.removeAttendee(campusId, asistente.id, nota.trim() || undefined, d)
			.then(() => onDone(d ? S.bajaOkDestino(d, m(prev.surplus)) : S.bajaOk))
			.catch(e => { setError(errorText(e)); setBusy(false); });
	};

	return (
		<Modal title={S.bajaTitulo(asistente.name)} onClose={onClose} ancho={480}>
			{!prev && !error ? <p className="wau-sec" style={{ marginTop: 0 }}>{S.cargando}</p> : null}
			{prev && conPago ? (
				<div className="wte-datos" style={{ marginBottom: 12 }}>
					{prev.removed.length > 1 ? <div style={{ gridColumn: "1 / -1" }}><span className="wau-lab">{S.bajaSalen}</span>
						{prev.removed.map(x => <div key={x.id} style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
							<span>{x.name}</span>{x.paid > 0.004 ? <span className="wau-num wau-sec">{m(x.paid)}</span> : null}</div>)}</div> : null}
					<div><span className="wau-lab">{S.bajaPago}</span><span className="wau-num">{m(prev.paid)}</span></div>
					{prev.retained > 0.004 ? <div><span className="wau-lab">{S.bajaSeQueda}</span><span className="wau-num">{m(prev.retained)}</span></div> : null}
					{sobra ? <div><span className="wau-lab">{S.bajaSobran}</span><span className="wau-num wau-oro"><b>{m(prev.surplus)}</b></span></div> : null}
				</div>
			) : null}
			{bloqueada ? <p className="wau-adv">{S.bajaSoloCaja}</p> : null}
			{prev && !bloqueada && sobra ? (
				<div style={{ marginBottom: 10 }}>
					<span className="wau-lab">{S.bajaDestino}</span>
					<div className="wau-chips" role="group" aria-label={S.bajaDestino} style={{ marginTop: 4, marginBottom: 6 }}>
						{destinos.map(d => <button key={d} type="button" className="wau-chip" aria-pressed={destino === d} onClick={() => setDestino(d)}>
							{S.destinos[d]}{d === "aplicar" ? <span className="wau-num"> · {m(totalPendiente)}</span> : null}</button>)}
					</div>
					{destino === "aplicar" ? <div className="wau-sec" style={{ fontSize: 13 }}>
						{pendientes.map(c => <div key={c.owedArticleId} style={{ display: "flex", justifyContent: "space-between", gap: 10 }}><span>{c.title}</span><span className="wau-num">{m(c.pending)}</span></div>)}
					</div> : null}
				</div>
			) : null}
			{prev && !bloqueada ? (
				<label className="wau-campo" style={{ marginBottom: 10 }}><span className="wau-lab">{notaObligatoria ? S.bajaNotaDevolucion : S.bajaNota}</span>
					<textarea className="wau-entrada" rows={3} maxLength={255} value={nota} onChange={e => setNota(e.target.value)} autoFocus={!sobra} /></label>
			) : null}
			{error ? <p className="wau-adv">{error}</p> : null}
			<div className="wau-acciones">
				<button type="button" className="wau-btn s" onClick={onClose} disabled={busy}>{S.volver}</button>
				{prev && !bloqueada ? <button type="button" className="wau-btn p" onClick={confirmar} disabled={busy || !valido}>{S.baja}</button> : null}
			</div>
		</Modal>
	);
};

/** Asistentes: no., tipo, mesa, pago, foto, QR, invitación y diploma en PDF, baja; reparto de mesas. */
const AsistentesTab: React.FC<Props> = ({ campusId, evento, soloLectura, reload, notify }) => {
	const http = HttpManager.getInstance();
	const rolDinero = useRolDinero(campusId);
	const [agregar, setAgregar] = useState<boolean>(false);
	const [hostInicial, setHostInicial] = useState<number | undefined>(undefined);
	const [baja, setBaja] = useState<EventAttendeeDTO | null>(null);
	const [busy, setBusy] = useState<boolean>(false);
	const [fotoDe, setFotoDe] = useState<EventAttendeeDTO | null>(null);
	const [verFoto, setVerFoto] = useState<{ nombre: string; url: string } | null>(null);
	const [verQr, setVerQr] = useState<EventAttendeeDTO | null>(null);
	const [repartir, setRepartir] = useState<boolean>(false);
	const [nombrar, setNombrar] = useState<EventAttendeeDTO | null>(null);
	const [conservar, setConservar] = useState<boolean>(false);
	const archivo = useRef<HTMLInputElement>(null);
	const vigentes = evento.attendees.filter(a => a.status === 1);
	const movil = useEsTelefono();
	const svgQr = useMemo(() => (verQr ? dibujarQr(verQr.qrCode) : ""), [verQr]);

	const pdf = (a: EventAttendeeDTO, tipo: "invitacion" | "diploma") => {
		const req = tipo === "invitacion" ? http.eventsService.invitationPdf(campusId, a.id) : http.eventsService.diplomaPdf(campusId, a.id);
		req.then(r => guardarBlob(r.data, `${tipo === "invitacion" ? "Invitacion" : "Diploma"}_${slug(a.name)}.pdf`))
			.catch(async e => notify(await blobErrorText(e), false));
	};
	const subir = (file: File | undefined) => {
		if (!file || !fotoDe) return;
		setBusy(true);
		http.eventsService.uploadPhoto(campusId, fotoDe.id, file)
			.then(() => { notify(S.fotoOk); reload(); })
			.catch(e => notify(errorText(e), false))
			.finally(() => { setBusy(false); setFotoDe(null); if (archivo.current) archivo.current.value = ""; });
	};
	const mostrarFoto = (a: EventAttendeeDTO) => http.eventsService.photo(campusId, a.id)
		.then(r => setVerFoto({ nombre: a.name, url: URL.createObjectURL(r.data) }))
		.catch(async e => notify(await blobErrorText(e), false));
	const mesa = (a: EventAttendeeDTO, v: string) => {
		const n = v.trim() === "" ? null : v.trim();
		if (n === (a.tableNo ?? null)) return;
		http.eventsService.updateAttendee(campusId, { attendeeId: a.id, tableNo: n }).then(reload).catch(e => notify(errorText(e), false));
	};
	const pagoManual = (a: EventAttendeeDTO, paid: boolean) =>
		http.eventsService.updateAttendee(campusId, { attendeeId: a.id, paid }).then(reload).catch(e => notify(errorText(e), false));
	const repartirMesas = () => {
		setBusy(true);
		http.eventsService.assignTables(campusId, evento.id, conservar)
			.then(() => { setRepartir(false); notify(S.mesasOk); reload(); })
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};

	const pago = (a: EventAttendeeDTO) => {
		if (a.freeByPackage) return <span className="wau-sec">{S.gratisPaquete}</span>;
		if (a.charge) return <><div className="wau-num">{dinero(evento.currency, a.charge.total)}</div>
			<div className={a.paid ? "wau-sec" : "wau-oro"} style={{ fontSize: 12.5 }}>{a.paid ? S.pagado : S.porPagar + " " + dinero(evento.currency, a.charge.pending)}</div></>;
		if (a.kind === "externo" && !soloLectura && a.status === 1 && rolDinero) {
			return <label style={{ display: "inline-flex", gap: 6, alignItems: "center", margin: 0, position: "static" }}>
				<input type="checkbox" checked={a.paid} onChange={e => pagoManual(a, e.target.checked)} />
				<span className={a.paid ? "" : "wau-oro"}>{a.paid ? S.pagado : S.porPagar}{a.price ? " " + dinero(evento.currency, a.price) : ""}</span></label>;
		}
		if (a.kind === "externo" && a.price) return <span className={a.paid ? "wau-sec" : "wau-oro"}>{a.paid ? S.pagado : S.porPagar} {dinero(evento.currency, a.price)}</span>;
		return <span className="wau-sec">{a.paid ? S.pagado : S.sinCobro}</span>;
	};
	const diploma = (a: EventAttendeeDTO) => {
		if (!a.diploma) return <span className="wau-sec">—</span>;
		if (a.diploma.ready) return <span className="wau-pastilla acento">{S.diplomaListo}</span>;
		return <span className="wau-pastilla oro" title={a.diploma.sessionsToRecover ? S.nPorReponer(a.diploma.sessionsToRecover) : undefined}>{S.falta(a.diploma.missing)}</span>;
	};
	const faltan = vigentes.filter(a => a.namePending).length;
	/** Subir el paquete de una graduada: agrega invitados y cobra solo el remanente; lo ya pagado no se toca. */
	const masInvitados = (a: EventAttendeeDTO) => !soloLectura && a.status === 1 && a.kind === "graduada"
		? <button type="button" className="wau-btn s mini" onClick={() => { setHostInicial(a.id); setAgregar(true); }}>{S.masInvitados}</button> : null;
	/** Invitado pagado sin nombre todavía: en oro y con su botón para ponérselo. */
	const nombre = (a: EventAttendeeDTO) => a.namePending && a.status === 1 ? <>
		<b className="wau-oro">{a.name}</b>{soloLectura ? null : <> <button type="button" className="wau-btn s mini" onClick={() => setNombrar(a)}>{S.ponerNombre}</button></>}
	</> : <b>{a.name}</b>;
	const botonQr = (a: EventAttendeeDTO) => a.status === 1 ? <button type="button" className="wte-qr" aria-label={S.qrDe(a.name)} title={S.verQr} onClick={() => setVerQr(a)}><IconoQr /></button> : null;

	return (
		<div className="wau-caja">
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
				<h3 style={{ margin: 0 }}>{S.tabs.asistentes} <span className="wau-sec wau-num">({S.vigentes(vigentes.length)})</span>
					{faltan ? <span className="wau-oro wau-num" style={{ fontSize: 14 }}> · {S.faltanNombres(faltan)}</span> : null}</h3>
				{soloLectura ? null : <div className="wau-acciones" style={{ margin: 0 }}>
					{vigentes.length > 0 && evento.tableSize ? <button type="button" className="wau-btn s" onClick={() => setRepartir(true)}>{S.repartirMesas}</button> : null}
					<button type="button" className="wau-btn p" onClick={() => { setHostInicial(undefined); setAgregar(true); }}>{S.agregarAsistente}</button>
				</div>}
			</div>
			<input ref={archivo} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={e => subir(e.target.files?.[0])} />
			{movil ? (
				/* En teléfono la tabla de nueve columnas se partía a media palabra: cada asistente pasa a una tarjeta. */
				<div>
					{evento.attendees.length === 0 ? <p className="wau-sec" style={{ margin: 0 }}>{S.sinAsistentes}</p> : evento.attendees.map((a, i) => (
						<div key={a.id} className="wau-tarjeta" style={a.status !== 1 ? { opacity: .55 } : undefined}>
							<div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}><span><span className="wau-num wau-sec">{i + 1}. </span>{nombre(a)}</span>{botonQr(a)}</div>
							<div className="linea">{a.kindLabel}{a.isChild ? " · " + S.esNino : ""}{a.hostName ? " · " + S.invitaA(a.hostName) : a.identityCode ? " · " + a.identityCode : ""}</div>
							{a.status !== 1 ? <div className="wau-oro" style={{ fontSize: 12.5 }}>{a.statusLabel}</div> : null}
							<div className="wau-fila" style={{ marginTop: 6, alignItems: "center" }}>
								<span className="linea">{S.colMesa}</span>
								{soloLectura || a.status !== 1 ? <span>{a.tableNo ?? "—"}</span> :
									<input key={a.tableNo ?? ""} className="wau-entrada wau-num" maxLength={10} style={{ width: 70, padding: "3px 6px" }} aria-label={S.colMesa} defaultValue={a.tableNo ?? ""} onBlur={e => mesa(a, e.target.value)} />}
							</div>
							<div style={{ marginTop: 6 }}>{pago(a)}</div>
							<div style={{ marginTop: 6 }}>{diploma(a)}</div>
							<div className="linea wau-num" style={{ marginTop: 6 }}>{S.colEntrada} {a.checkedInAt ? horaLocal(a.checkedInAt) : "—"}</div>
							<div className="wau-acciones" style={{ justifyContent: "flex-start", flexWrap: "wrap", marginTop: 8 }}>
								{a.hasPhoto ? <button type="button" className="wau-btn s mini" onClick={() => mostrarFoto(a)}>{S.verFoto}</button> : null}
								{soloLectura || a.status !== 1 ? null : <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => { setFotoDe(a); archivo.current?.click(); }}>{a.hasPhoto ? S.cambiarFoto : S.subirFoto}</button>}
								{masInvitados(a)}{a.status === 1 ? <button type="button" className="wau-btn s mini" onClick={() => pdf(a, "invitacion")}>{S.invitacionPdf}</button> : null}
								{a.status === 1 && a.diploma ? <button type="button" className="wau-btn s mini" disabled={!a.diploma.ready} onClick={() => pdf(a, "diploma")}>{S.diplomaPdf}</button> : null}
								{!soloLectura && a.status === 1 ? <button type="button" className="wau-btn s mini" onClick={() => setBaja(a)}>{S.baja}</button> : null}
							</div>
						</div>
					))}
				</div>
			) : (
			<div className="wau-tabla">
				<table>
					<thead><tr>{[S.colNo, S.colNombre, S.colTipo, S.colMesa, S.colPago, S.colFoto, S.colDiploma, S.colEntrada, ""].map((t, i) => <th key={i} className={i === 0 || i === 4 ? "der" : undefined} style={{ whiteSpace: "nowrap" }}>{t}</th>)}</tr></thead>
					<tbody>
						{evento.attendees.length === 0 ? <tr><td colSpan={9} className="wau-sec" style={{ textAlign: "center", padding: 18 }}>{S.sinAsistentes}</td></tr> : evento.attendees.map((a, i) => (
							<tr key={a.id} style={a.status !== 1 ? { opacity: .55 } : undefined}>
								<td className="der wau-num">{i + 1}</td>
								<td style={{ minWidth: 170 }}>{nombre(a)}<div className="wau-sec" style={{ fontSize: 12.5 }}>{a.hostName ? S.invitaA(a.hostName) : a.identityCode || ""}</div></td>
								<td style={{ whiteSpace: "nowrap" }}>{a.kindLabel}{a.isChild ? " · " + S.esNino : ""}{a.status !== 1 ? <div className="wau-oro" style={{ fontSize: 12.5 }}>{a.statusLabel}</div> : null}</td>
								<td>{soloLectura || a.status !== 1 ? (a.tableNo ?? "—") :
									/* key = mesa guardada: al repartir mesas el campo (no controlado) toma el valor nuevo */
									<input key={a.tableNo ?? ""} className="wau-entrada wau-num" maxLength={10} style={{ width: 70, padding: "3px 6px" }} aria-label={S.colMesa} defaultValue={a.tableNo ?? ""} onBlur={e => mesa(a, e.target.value)} />}</td>
								<td className="der">{pago(a)}</td>
								<td style={{ whiteSpace: "nowrap" }}>
									{a.hasPhoto ? <button type="button" className="wau-btn s mini" onClick={() => mostrarFoto(a)}>{S.verFoto}</button> : null}{" "}
									{soloLectura || a.status !== 1 ? null : <button type="button" className="wau-btn s mini" disabled={busy} onClick={() => { setFotoDe(a); archivo.current?.click(); }}>{a.hasPhoto ? S.cambiarFoto : S.subirFoto}</button>}
								</td>
								<td>{diploma(a)}</td>
								<td className="wau-num" style={{ whiteSpace: "nowrap", fontSize: 13 }}>{a.checkedInAt ? horaLocal(a.checkedInAt) : "—"}</td>
								<td><div style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end", alignItems: "center", maxWidth: 300, marginLeft: "auto" }}>
									{botonQr(a)}{" "}
									{masInvitados(a)}{a.status === 1 ? <button type="button" className="wau-btn s mini" onClick={() => pdf(a, "invitacion")}>{S.invitacionPdf}</button> : null}{" "}
									{a.status === 1 && a.diploma ? <button type="button" className="wau-btn s mini" disabled={!a.diploma.ready} onClick={() => pdf(a, "diploma")}>{S.diplomaPdf}</button> : null}{" "}
									{!soloLectura && a.status === 1 ? <button type="button" className="wau-btn s mini" onClick={() => setBaja(a)}>{S.baja}</button> : null}
								</div></td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			)}
			{agregar ? <AgregarModal campusId={campusId} evento={evento} rolDinero={rolDinero} hostInicial={hostInicial} onClose={() => { setAgregar(false); reload(); }} onDone={(n) => { setAgregar(false); notify(n > 1 ? S.invitadosAgregados(n) : S.agregado); reload(); }} /> : null}
			{baja ? <BajaModal campusId={campusId} evento={evento} asistente={baja} onClose={() => setBaja(null)} onDone={(t) => { setBaja(null); notify(t); reload(); }} /> : null}
			{nombrar ? <PromptModal title={S.nombreTitulo(nombrar.name)} label={S.colNombre} confirm={S.guardar} busy={busy} onCancel={() => setNombrar(null)}
				onConfirm={(n) => {
					setBusy(true);
					http.eventsService.updateAttendee(campusId, { attendeeId: nombrar.id, name: n })
						.then(() => { setNombrar(null); reload(); })
						.catch(e => notify(errorText(e), false))
						.finally(() => setBusy(false));
				}} /> : null}
			{repartir ? (
				<Modal title={S.repartirTitulo} onClose={() => setRepartir(false)} ancho={420}>
					<div className="wte-datos" style={{ marginBottom: 12 }}>
						<div><span className="wau-lab">{S.porMesa}</span><span className="wau-num">{evento.tableSize}</span></div>
						<div><span className="wau-lab">{S.tabs.asistentes}</span><span className="wau-num">{vigentes.length}</span></div>
					</div>
					<label style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 12px 0", position: "static" }}>
						<input type="checkbox" checked={conservar} onChange={e => setConservar(e.target.checked)} /><span>{S.conservarManuales}</span></label>
					<div className="wau-acciones">
						<button type="button" className="wau-btn s" onClick={() => setRepartir(false)} disabled={busy}>{S.volver}</button>
						<button type="button" className="wau-btn p" onClick={repartirMesas} disabled={busy}>{S.repartir}</button>
					</div>
				</Modal>
			) : null}
			{verQr ? <Modal title={verQr.name} onClose={() => setVerQr(null)} ancho={360}>
				<div className="wte-qr-grande" dangerouslySetInnerHTML={{ __html: svgQr }} />
				<div className="wte-codigo">{verQr.qrCode}</div>
				<div className="wau-sec" style={{ textAlign: "center", marginTop: 4, fontSize: 13.5 }}>{verQr.kindLabel}{verQr.tableNo ? " · " + S.colMesa + " " + verQr.tableNo : ""}</div>
			</Modal> : null}
			{verFoto ? <Modal title={verFoto.nombre} onClose={() => { URL.revokeObjectURL(verFoto.url); setVerFoto(null); }}>
				<img src={verFoto.url} alt={verFoto.nombre} style={{ maxWidth: "100%", borderRadius: 8 }} />
			</Modal> : null}
		</div>
	);
};

export default AsistentesTab;
